import { useEffect, useState } from "react";
import {
  Users, Receipt, Video, LayoutDashboard,
  Check, X, Link2, Plus, Loader2, ExternalLink, AlertTriangle, Sparkles,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-stone-200 bg-white p-5">
      <Icon size={18} strokeWidth={1.5} className="text-amber-700" />
      <div className="font-serif text-2xl text-stone-900 mt-3">{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  pending_review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-stone-200 text-stone-700 border-stone-300",
  declined: "bg-red-100 text-red-800 border-red-200",
};

function ApplicationsTab({ applications, onChanged }) {
  const [approveTarget, setApproveTarget] = useState(null);
  const [form, setForm] = useState({ package_id: "", package_name: "", monthly_fee: "", sessions_per_month: "", term_months: "3", start_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const decline = async (id) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status: "declined" });
      toast.success("Application declined");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const approve = async () => {
    if (!form.package_name || !form.monthly_fee) {
      toast.error("Package name and monthly fee are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/contracts", {
        client_user_id: approveTarget.user_id,
        package_id: form.package_id || approveTarget.package_interest,
        package_name: form.package_name,
        monthly_fee: parseFloat(form.monthly_fee),
        sessions_per_month: form.sessions_per_month ? parseInt(form.sessions_per_month, 10) : null,
        term_months: parseInt(form.term_months, 10),
        start_date: new Date(form.start_date).toISOString(),
      });
      toast.success("Client approved & contract created");
      setApproveTarget(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (applications.length === 0) {
    return <p className="text-sm text-stone-500" data-testid="no-applications">No applications yet.</p>;
  }

  return (
    <div className="space-y-4" data-testid="applications-list">
      {applications.map((a) => (
        <div key={a.id} data-testid={`application-row-${a.id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-stone-900">{a.name}</span>
                <Badge className={`rounded-full border text-xs ${STATUS_STYLE[a.status]}`}>{a.status.replace("_", " ")}</Badge>
              </div>
              <div className="text-xs text-stone-500 mt-1">{a.email} · {a.phone} · {a.city_country}</div>
              <div className="text-xs text-stone-500 mt-0.5">Package interest: {a.package_interest.replace(/_/g, " ")}</div>
            </div>
            {a.status === "pending_review" && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  data-testid={`approve-application-${a.id}`}
                  onClick={() => {
                    setApproveTarget(a);
                    setForm((f) => ({ ...f, package_id: a.package_interest, package_name: a.package_interest.replace(/_/g, " ") }));
                  }}
                  className="rounded-full bg-green-700 hover:bg-green-800 text-stone-50 h-8"
                >
                  <Check size={13} strokeWidth={1.5} className="mr-1" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`decline-application-${a.id}`}
                  onClick={() => decline(a.id)}
                  className="rounded-full border-red-200 text-red-700 hover:bg-red-50 h-8"
                >
                  <X size={13} strokeWidth={1.5} className="mr-1" /> Decline
                </Button>
              </div>
            )}
          </div>

          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-stone-400 uppercase tracking-wide">Biggest challenge</div>
              <p className="text-stone-700 mt-1">{a.biggest_challenge}</p>
            </div>
            <div>
              <div className="text-xs text-stone-400 uppercase tracking-wide">Expected outcome</div>
              <p className="text-stone-700 mt-1">{a.expected_outcome}</p>
            </div>
          </div>

          {a.ai_summary && (
            <div data-testid={`ai-summary-${a.id}`} className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-800">
                <Sparkles size={13} strokeWidth={1.5} /> AI pre-screening
              </div>
              <p className="text-sm text-stone-700 mt-2">{a.ai_summary}</p>
              {a.ai_risk_flags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {a.ai_risk_flags.map((f) => (
                    <Badge key={f} className="rounded-full bg-red-100 text-red-700 border border-red-200 text-[10px]">
                      <AlertTriangle size={10} strokeWidth={1.5} className="mr-1" /> {f.replace(/_/g, " ")}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent data-testid="approve-contract-dialog">
          <DialogHeader>
            <DialogTitle>Approve {approveTarget?.name} & create contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Package name</Label>
              <Input data-testid="contract-package-name-input" value={form.package_name} onChange={(e) => setForm((f) => ({ ...f, package_name: e.target.value }))} className="mt-1.5 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Monthly fee (CAD)</Label>
                <Input data-testid="contract-fee-input" type="number" value={form.monthly_fee} onChange={(e) => setForm((f) => ({ ...f, monthly_fee: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label>Sessions/month</Label>
                <Input data-testid="contract-sessions-input" type="number" value={form.sessions_per_month} onChange={(e) => setForm((f) => ({ ...f, sessions_per_month: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Term (months)</Label>
                <Input data-testid="contract-term-input" type="number" value={form.term_months} onChange={(e) => setForm((f) => ({ ...f, term_months: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label>Start date</Label>
                <Input data-testid="contract-start-input" type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="contract-save-button" disabled={saving} onClick={approve} className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
              {saving ? "Saving..." : "Approve & create contract"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientsTab({ clients, onChanged }) {
  const [clickupEdits, setClickupEdits] = useState({});

  const saveClickup = async (userId) => {
    try {
      await api.patch(`/admin/clients/${userId}/clickup`, { clickup_url: clickupEdits[userId] });
      toast.success("ClickUp link saved");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (appId, status) => {
    try {
      await api.patch(`/admin/applications/${appId}`, { status });
      toast.success("Status updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (clients.length === 0) {
    return <p className="text-sm text-stone-500" data-testid="no-clients">No active clients yet — approve an application to get started.</p>;
  }

  return (
    <div className="space-y-4" data-testid="clients-list">
      {clients.map((c) => (
        <div key={c.id} data-testid={`client-row-${c.user_id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-medium text-stone-900">{c.name}</div>
              <div className="text-xs text-stone-500 mt-0.5">{c.email}</div>
              {c.contract && (
                <div className="text-xs text-stone-500 mt-1">{c.contract.package_name} · ${c.contract.monthly_fee.toLocaleString()} CAD/mo</div>
              )}
            </div>
            <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
              <SelectTrigger data-testid={`client-status-select-${c.user_id}`} className="w-40 rounded-full h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Link2 size={14} strokeWidth={1.5} className="text-stone-400" />
            <Input
              data-testid={`clickup-input-${c.user_id}`}
              placeholder="https://app.clickup.com/..."
              defaultValue={c.clickup_url || ""}
              onChange={(e) => setClickupEdits((s) => ({ ...s, [c.user_id]: e.target.value }))}
              className="rounded-xl h-9 text-sm max-w-md"
            />
            <Button size="sm" data-testid={`clickup-save-${c.user_id}`} variant="outline" className="rounded-full h-9" onClick={() => saveClickup(c.user_id)}>
              Save
            </Button>
            {c.clickup_url && (
              <a href={c.clickup_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} strokeWidth={1.5} className="text-amber-700" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PaymentsTab({ payments, clients, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_user_id: "", amount: "", description: "", period_label: "" });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.client_user_id || !form.amount || !form.description) {
      toast.error("Client, amount and description are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/payments", { ...form, amount: parseFloat(form.amount) });
      toast.success("Invoice created");
      setOpen(false);
      setForm({ client_user_id: "", amount: "", description: "", period_label: "" });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-button" className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
              <Plus size={15} strokeWidth={1.5} className="mr-1.5" /> New invoice
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-invoice-dialog">
            <DialogHeader><DialogTitle>Create invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Client</Label>
                <Select value={form.client_user_id} onValueChange={(v) => setForm((f) => ({ ...f, client_user_id: v }))}>
                  <SelectTrigger data-testid="invoice-client-select" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (CAD)</Label>
                <Input data-testid="invoice-amount-input" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label>Description</Label>
                <Input data-testid="invoice-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="March coaching fee" />
              </div>
              <div>
                <Label>Period label (optional)</Label>
                <Input data-testid="invoice-period-input" value={form.period_label} onChange={(e) => setForm((f) => ({ ...f, period_label: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="March 2026" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="invoice-save-button" disabled={saving} onClick={create} className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
                {saving ? "Creating..." : "Create invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {payments.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="no-payments">No invoices yet.</p>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100">
          {payments.map((p) => (
            <div key={p.id} data-testid={`admin-payment-row-${p.id}`} className="flex items-center justify-between p-4">
              <div>
                <div className="text-sm text-stone-900">{p.description}</div>
                <div className="text-xs text-stone-500 mt-0.5">{p.period_label || p.created_at.slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-serif text-stone-900">${p.amount.toLocaleString()} CAD</span>
                <Badge className={`rounded-full border text-xs ${p.status === "paid" ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                  {p.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServicesTab({ services, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/services/${editing.id}`, {
        title: editing.title, description: editing.description, type: editing.type,
        price: editing.price === "" ? null : parseFloat(editing.price),
        price_unit: editing.price_unit, duration_minutes: editing.duration_minutes ? parseInt(editing.duration_minutes, 10) : null,
        sessions_per_month: editing.sessions_per_month ? parseInt(editing.sessions_per_month, 10) : null,
        category: editing.category,
      });
      toast.success("Service updated");
      setEditing(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="services-list">
      {services.map((s) => (
        <div key={s.id} data-testid={`service-row-${s.id}`} className="rounded-2xl border border-stone-200 bg-white p-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-stone-900">{s.title}</div>
            <div className="text-xs text-stone-500 mt-0.5">{s.price ? `$${s.price.toLocaleString()} CAD ${s.price_unit || ""}` : s.price_unit || "Custom quote"}</div>
          </div>
          <Button size="sm" variant="outline" data-testid={`edit-service-${s.id}`} className="rounded-full" onClick={() => setEditing({ ...s })}>
            Edit
          </Button>
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-service-dialog">
          <DialogHeader><DialogTitle>Edit service</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input data-testid="service-title-input" value={editing.title} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="service-description-input" value={editing.description} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} className="mt-1.5 rounded-xl" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Price (CAD, blank = custom quote)</Label>
                  <Input data-testid="service-price-input" type="number" value={editing.price ?? ""} onChange={(e) => setEditing((s) => ({ ...s, price: e.target.value }))} className="mt-1.5 rounded-xl" />
                </div>
                <div>
                  <Label>Price unit</Label>
                  <Input data-testid="service-price-unit-input" value={editing.price_unit || ""} onChange={(e) => setEditing((s) => ({ ...s, price_unit: e.target.value }))} className="mt-1.5 rounded-xl" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="service-save-button" disabled={saving} onClick={save} className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SessionsTab({ sessions, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", scheduled_at: "", duration_minutes: "60" });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/live", {
        title: form.title,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        duration_minutes: parseInt(form.duration_minutes, 10),
      });
      toast.success("Live session created");
      setOpen(false);
      setForm({ title: "", scheduled_at: "", duration_minutes: "60" });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-session-button" className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
              <Plus size={15} strokeWidth={1.5} className="mr-1.5" /> New live session
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-session-dialog">
            <DialogHeader><DialogTitle>Create live session</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input data-testid="session-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5 rounded-xl" placeholder="Group coaching — week 1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Scheduled at</Label>
                  <Input data-testid="session-datetime-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} className="mt-1.5 rounded-xl" />
                </div>
                <div>
                  <Label>Duration (min)</Label>
                  <Input data-testid="session-duration-input" type="number" value={form.duration_minutes} onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))} className="mt-1.5 rounded-xl" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="session-save-button" disabled={saving} onClick={create} className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
                {saving ? "Creating..." : "Create session"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {sessions.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="no-sessions">No live sessions scheduled.</p>
      ) : (
        <div className="space-y-3" data-testid="sessions-list">
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-row-${s.id}`} className="rounded-2xl border border-stone-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-sm font-medium text-stone-900">{s.title}</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : "Not scheduled"} · {s.duration_minutes} min
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="rounded-full bg-stone-100 text-stone-700 border border-stone-200 capitalize">{s.status}</Badge>
                <a href={s.room_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="rounded-full h-8">
                    <Video size={13} strokeWidth={1.5} className="mr-1.5" /> Room
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPortal() {
  const [dashboard, setDashboard] = useState(null);
  const [applications, setApplications] = useState([]);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [services, setServices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [d, a, c, p, s, ls] = await Promise.all([
        api.get("/dashboard/admin"),
        api.get("/admin/applications"),
        api.get("/admin/clients"),
        api.get("/admin/payments"),
        api.get("/services"),
        api.get("/live/mine"),
      ]);
      setDashboard(d.data);
      setApplications(a.data);
      setClients(c.data);
      setPayments(p.data);
      setServices(s.data);
      setSessions(ls.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-dashboard-loading">
        <Loader2 className="animate-spin text-amber-700" size={28} strokeWidth={1.5} />
      </div>
    );
  }

  const { stats } = dashboard;

  return (
    <div data-testid="admin-portal" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Owner Studio</div>
        <h1 className="font-serif text-4xl text-stone-900 tracking-tight">Welcome back, Hufrey.</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="admin-stat-applications" icon={Users} label="Total applications" value={stats.total_applications} />
        <StatCard testid="admin-stat-pending" icon={LayoutDashboard} label="Pending review" value={stats.pending_review} />
        <StatCard testid="admin-stat-clients" icon={Users} label="Active clients" value={stats.active_clients} />
        <StatCard testid="admin-stat-revenue" icon={Receipt} label="Revenue (CAD)" value={`$${stats.total_revenue.toLocaleString()}`} />
        <StatCard testid="admin-stat-sessions" icon={Video} label="Upcoming sessions" value={stats.upcoming_sessions} />
      </div>

      <Tabs defaultValue="applications">
        <TabsList className="rounded-full bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="applications" data-testid="tab-applications" className="rounded-full">Applications</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients" className="rounded-full">Clients</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-full">Payments</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services" className="rounded-full">Services & Pricing</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions" className="rounded-full">Live Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="applications" className="mt-6">
          <ApplicationsTab applications={applications} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="clients" className="mt-6">
          <ClientsTab clients={clients} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="payments" className="mt-6">
          <PaymentsTab payments={payments} clients={clients} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="services" className="mt-6">
          <ServicesTab services={services} onChanged={loadAll} />
        </TabsContent>
        <TabsContent value="sessions" className="mt-6">
          <SessionsTab sessions={sessions} onChanged={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
